import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  ColumnFiltersState,
} from "@tanstack/react-table";
import * as XLSX from "xlsx";
import useFetch from "../../hooks/use-fetch";
import Config from "../../config";
import { CurrentInventoryResult, CurrentInventoryPallet } from "./dtos/current-inventory-result.dto";
import { SkuGridRow, PalletGridRow } from "./dtos/current-inventory-models";
import "./current-inventory-report.css";

// Extend ColumnMeta to include align property
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    align?: 'left' | 'right' | 'center';
  }
}

const CurrentInventoryReport = () => {
  const [skuData, setSkuData] = useState<SkuGridRow[]>([]);
  const [palletData, setPalletData] = useState<PalletGridRow[]>([]);
  const [skuSorting, setSkuSorting] = useState<SortingState>([]);
  const [palletSorting, setPalletSorting] = useState<SortingState>([]);
  const [skuFilters, setSkuFilters] = useState<ColumnFiltersState>([]);
  const [palletFilters, setPalletFilters] = useState<ColumnFiltersState>([]);

  const url = `${Config.baseUrl}/api/pallet/current-inventory`;
  const { data, loading, error } = useFetch<CurrentInventoryResult>(url, [], {});

  // Helper function to calculate days since creation
  const calculateDaysAgo = (dateString: string): number => {
    const created = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Transform data for SKU view (aggregation)
  const transformToSkuData = (pallets: CurrentInventoryPallet[]): SkuGridRow[] => {
    const skuMap = new Map<string, SkuGridRow>();

    pallets.forEach(pallet => {
      pallet.items.forEach(item => {
        if (!skuMap.has(item.sku)) {
          skuMap.set(item.sku, {
            sku: item.sku,
            frameStyle: item.frameStyle,
            frameCategory: item.frameCategory,
            frameProfile: item.frameProfile,
            feetPerBox: item.feetPerBox,
            totalBoxes: 0,
            totalFeet: 0
          });
        }

        const existing = skuMap.get(item.sku)!;
        existing.totalBoxes += item.quantity;
        existing.totalFeet += item.totalFeet;
      });
    });

    return Array.from(skuMap.values()).sort((a, b) => {
      if (a.frameCategory !== b.frameCategory) {
        return a.frameCategory.localeCompare(b.frameCategory);
      }
      if (a.frameProfile !== b.frameProfile) {
        return a.frameProfile.localeCompare(b.frameProfile);
      }
      if (a.frameStyle !== b.frameStyle) {
        return a.frameStyle.localeCompare(b.frameStyle);
      }
      return a.sku.localeCompare(b.sku);
    });
  };

  // Transform data for Pallet view (flattened list)
  const transformToPalletData = (pallets: CurrentInventoryPallet[]): PalletGridRow[] => {
    const palletRows = pallets.flatMap(pallet =>
      pallet.items.map(item => ({
        palletId: pallet.palletId,
        palletCode: pallet.palletCode,
        location: pallet.location,
        age: calculateDaysAgo(pallet.createdDateTimeLocal),
        sku: item.sku,
        frameStyle: item.frameStyle,
        frameCategory: item.frameCategory,
        frameProfile: item.frameProfile,
        feetPerBox: item.feetPerBox,
        quantity: item.quantity,
        totalFeet: item.totalFeet
      }))
    );

    return palletRows.sort((a, b) => {
      if (a.palletCode !== b.palletCode) {
        return a.palletCode.localeCompare(b.palletCode);
      }
      return a.sku.localeCompare(b.sku);
    });
  };

  // Transform data when API response changes
  useEffect(() => {
    if (data && data.activePallets) {
      // Debug: Log sample item to check frameProfile
      if (data.activePallets.length > 0 && data.activePallets[0].items.length > 0) {
        console.log('Sample pallet item:', data.activePallets[0].items[0]);
      }
      setSkuData(transformToSkuData(data.activePallets));
      setPalletData(transformToPalletData(data.activePallets));
    }
  }, [data]);

  // SKU Table Column Definitions
  const skuColumns: ColumnDef<SkuGridRow>[] = [
    {
      accessorKey: "sku",
      header: "SKU",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "frameCategory",
      header: "Frame Category",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "frameProfile",
      header: "Frame Profile",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "frameStyle",
      header: "Frame Style",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "feetPerBox",
      header: "Feet Per Box",
      cell: info => info.getValue(),
      meta: { align: 'right' },
    },
    {
      accessorKey: "totalBoxes",
      header: "Total Boxes",
      cell: info => info.getValue(),
      meta: { align: 'right' },
    },
    {
      accessorKey: "totalFeet",
      header: "Total Feet",
      cell: info => info.getValue(),
      meta: { align: 'right' },
    },
  ];

  // Pallet Table Column Definitions
  const palletColumns: ColumnDef<PalletGridRow>[] = [
    {
      accessorKey: "palletCode",
      header: "Pallet Code",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "location",
      header: "Location",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "age",
      header: "Age (days)",
      cell: info => info.getValue(),
      meta: { align: 'right' },
    },
    {
      accessorKey: "frameCategory",
      header: "Frame Category",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "frameProfile",
      header: "Frame Profile",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "frameStyle",
      header: "Frame Style",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "sku",
      header: "SKU",
      cell: info => info.getValue(),
    },
    {
      accessorKey: "feetPerBox",
      header: "Feet Per Box",
      cell: info => info.getValue(),
      meta: { align: 'right' },
    },
    {
      accessorKey: "quantity",
      header: "Quantity (boxes)",
      cell: info => info.getValue(),
      meta: { align: 'right' },
    },
    {
      accessorKey: "totalFeet",
      header: "Total Feet",
      cell: info => info.getValue(),
      meta: { align: 'right' },
    },
  ];

  // SKU Table Instance
  const skuTable = useReactTable({
    data: skuData,
    columns: skuColumns,
    state: {
      sorting: skuSorting,
      columnFilters: skuFilters,
    },
    onSortingChange: setSkuSorting,
    onColumnFiltersChange: setSkuFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Pallet Table Instance
  const palletTable = useReactTable({
    data: palletData,
    columns: palletColumns,
    state: {
      sorting: palletSorting,
      columnFilters: palletFilters,
    },
    onSortingChange: setPalletSorting,
    onColumnFiltersChange: setPalletFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Export to Excel using xlsx library
  const exportSkuToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(skuData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SKU Inventory");
    XLSX.writeFile(workbook, `current-inventory-by-sku-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportPalletToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(palletData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pallet Inventory");
    XLSX.writeFile(workbook, `current-inventory-by-pallet-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Copy to clipboard
  const copySkuToClipboard = () => {
    const headers = skuColumns.map(col => String(col.header)).join('\t');
    const rows = skuData.map(row =>
      skuColumns.map(col => {
        const key = (col as any).accessorKey as keyof SkuGridRow;
        return row[key];
      }).join('\t')
    ).join('\n');
    const text = headers + '\n' + rows;
    navigator.clipboard.writeText(text);
  };

  const copyPalletToClipboard = () => {
    const headers = palletColumns.map(col => String(col.header)).join('\t');
    const rows = palletData.map(row =>
      palletColumns.map(col => {
        const key = (col as any).accessorKey as keyof PalletGridRow;
        return row[key];
      }).join('\t')
    ).join('\n');
    const text = headers + '\n' + rows;
    navigator.clipboard.writeText(text);
  };

  // Loading state
  if (loading) {
    return (
      <Card title="Current Inventory">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <ProgressSpinner />
          <p>Loading current inventory...</p>
        </div>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card title="Current Inventory">
        <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
          <p>Error loading inventory: {String(error) || 'Unknown error'}</p>
        </div>
      </Card>
    );
  }

  // Empty state
  if (!data || !data.activePallets || data.activePallets.length === 0) {
    return (
      <Card title="Current Inventory">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>No active pallets found in inventory.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Current Inventory">
      {data.lastUpdated && (
        <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
          Last Updated: {new Date(data.lastUpdated).toLocaleString()}
        </div>
      )}

      <TabView>
        <TabPanel header="By SKU">
          <div style={{ marginBottom: '1rem' }}>
            <Button
              label="Export to Excel"
              icon="pi pi-file-excel"
              onClick={exportSkuToExcel}
              style={{ marginRight: '0.5rem' }}
            />
            <Button
              label="Copy to Clipboard"
              icon="pi pi-copy"
              onClick={copySkuToClipboard}
              style={{ marginRight: '0.5rem' }}
            />
            <Button
              label="Clear Filters"
              icon="pi pi-filter-slash"
              onClick={() => skuTable.resetColumnFilters()}
              severity="secondary"
            />
          </div>

          <div className="inventory-table-container">
            <table className="inventory-table">
              <thead>
                {skuTable.getHeaderGroups().map(headerGroup => (
                  <>
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className={header.column.columnDef.meta?.align === 'right' ? 'align-right' : ''}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              {...{
                                className: header.column.getCanSort() ? 'sortable-header' : '',
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                    <tr key={`${headerGroup.id}-filter`} className="filter-row">
                      {headerGroup.headers.map(header => (
                        <th key={header.id}>
                          {header.column.getCanFilter() ? (
                            <input
                              type="text"
                              value={(header.column.getFilterValue() ?? '') as string}
                              onChange={e => header.column.setFilterValue(e.target.value)}
                              placeholder={`Filter...`}
                              className="filter-input"
                            />
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  </>
                ))}
              </thead>
              <tbody>
                {skuTable.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className={cell.column.columnDef.meta?.align === 'right' ? 'align-right' : ''}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan={5} style={{ fontWeight: 'bold' }}>Totals:</td>
                  <td className="align-right" style={{ fontWeight: 'bold' }}>
                    {skuData.reduce((sum, row) => sum + row.totalBoxes, 0).toLocaleString()}
                  </td>
                  <td className="align-right" style={{ fontWeight: 'bold' }}>
                    {skuData.reduce((sum, row) => sum + row.totalFeet, 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </TabPanel>

        <TabPanel header="By Pallet">
          <div style={{ marginBottom: '1rem' }}>
            <Button
              label="Export to Excel"
              icon="pi pi-file-excel"
              onClick={exportPalletToExcel}
              style={{ marginRight: '0.5rem' }}
            />
            <Button
              label="Copy to Clipboard"
              icon="pi pi-copy"
              onClick={copyPalletToClipboard}
              style={{ marginRight: '0.5rem' }}
            />
            <Button
              label="Clear Filters"
              icon="pi pi-filter-slash"
              onClick={() => palletTable.resetColumnFilters()}
              severity="secondary"
            />
          </div>

          <div className="inventory-table-container">
            <table className="inventory-table">
              <thead>
                {palletTable.getHeaderGroups().map(headerGroup => (
                  <>
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className={header.column.columnDef.meta?.align === 'right' ? 'align-right' : ''}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              {...{
                                className: header.column.getCanSort() ? 'sortable-header' : '',
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                    <tr key={`${headerGroup.id}-filter`} className="filter-row">
                      {headerGroup.headers.map(header => (
                        <th key={header.id}>
                          {header.column.getCanFilter() ? (
                            <input
                              type="text"
                              value={(header.column.getFilterValue() ?? '') as string}
                              onChange={e => header.column.setFilterValue(e.target.value)}
                              placeholder={`Filter...`}
                              className="filter-input"
                            />
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  </>
                ))}
              </thead>
              <tbody>
                {palletTable.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className={cell.column.columnDef.meta?.align === 'right' ? 'align-right' : ''}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan={8} style={{ fontWeight: 'bold' }}>Totals:</td>
                  <td className="align-right" style={{ fontWeight: 'bold' }}>
                    {palletData.reduce((sum, row) => sum + row.quantity, 0).toLocaleString()}
                  </td>
                  <td className="align-right" style={{ fontWeight: 'bold' }}>
                    {palletData.reduce((sum, row) => sum + row.totalFeet, 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </TabPanel>
      </TabView>
    </Card>
  );
};

export default CurrentInventoryReport;
